import React, { createContext, useState, useEffect, useContext } from 'react';
import config from '../config/config';

// Create the auth context
const AuthContext = createContext();

// Custom hook to use the auth context
export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [accessToken, setAccessToken] = useState(null);
  const [refreshToken, setRefreshToken] = useState(null);
  const [tokenExpiry, setTokenExpiry] = useState(null);
  const [features, setFeatures] = useState([]);
  const [adminProfile, setAdminProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  // Load token from localStorage on initial mount
  useEffect(() => {
    const loadAuthState = async () => {
      try {
        const storedAccessToken = localStorage.getItem('accessToken');
        const storedRefreshToken = localStorage.getItem('refreshToken');
        const storedExpiry = localStorage.getItem('tokenExpiry');
        const storedFeatures = localStorage.getItem('features');
        const storedProfile = localStorage.getItem('adminProfile');

        if (storedAccessToken && storedRefreshToken && storedExpiry) {
          const expiryTime = parseInt(storedExpiry, 10);
          const now = Date.now();

          if (expiryTime > now) {
            // Token is still valid
            setAccessToken(storedAccessToken);
            setRefreshToken(storedRefreshToken);
            setTokenExpiry(expiryTime);
            setFeatures(storedFeatures ? JSON.parse(storedFeatures) : []);
            setAdminProfile(storedProfile ? JSON.parse(storedProfile) : null);
            setIsAuthenticated(true);
            
            // Schedule refresh before expiry
            const timeUntilRefresh = expiryTime - now - 60000; // Refresh 1 minute before expiry
            const refreshTimer = setTimeout(() => refreshAccessToken(storedRefreshToken), 
              timeUntilRefresh > 0 ? timeUntilRefresh : 0);
              
            // Return cleanup function to clear the timer
            return () => clearTimeout(refreshTimer);
          } else {
            // Token is expired, try to refresh
            await refreshAccessToken(storedRefreshToken);
          }
        }
      } catch (error) {
        console.error("Error loading auth state:", error);
      } finally {
        // Always ensure loading is set to false
        setLoading(false);
      }
    };

    loadAuthState();
    
    // Add event listener for navigation
    const handleNavigation = () => {
      // Reset loading state on navigation
      setLoading(false);
    };
    
    window.addEventListener('popstate', handleNavigation);
    
    return () => {
      window.removeEventListener('popstate', handleNavigation);
    };
  }, []);

  // Login function
  const login = async (email, password, captchaResponse = null) => {
    try {
      setLoading(true);
      
      // Prepare the request body
      const requestBody = { email, password };
      
      // Add CAPTCHA response if provided
      if (captchaResponse) {
        requestBody.recaptchaResponse = captchaResponse;
      }
      
      const response = await fetch(`${config.API_BASE_URL}/um/admin/v1/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();

      if (data.status === 'success') {
        const { accessToken, refreshToken, expiresIn, features, role } = data.data;
        const expiryTime = Date.now() + expiresIn * 1000;
        const userFeatures = features || [];
        const profile = { email, role: role || 'ADMIN' };
        
        // Save to state
        setAccessToken(accessToken);
        setRefreshToken(refreshToken);
        setTokenExpiry(expiryTime);
        setFeatures(userFeatures);
        setAdminProfile(profile);
        setIsAuthenticated(true);
        
        // Save to localStorage
        localStorage.setItem('accessToken', accessToken);
        localStorage.setItem('refreshToken', refreshToken);
        localStorage.setItem('tokenExpiry', expiryTime.toString());
        localStorage.setItem('features', JSON.stringify(userFeatures));
        localStorage.setItem('adminProfile', JSON.stringify(profile));
        
        // Schedule token refresh
        const timeUntilRefresh = expiresIn * 1000 - 60000; // Refresh 1 minute before expiry
        setTimeout(refreshAccessToken, timeUntilRefresh);
        
        return { success: true };
      } else {
        return { success: false, message: data.message || 'Login failed' };
      }
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, message: 'Network or server error' };
    } finally {
      setLoading(false);
    }
  };

  // Refresh token function
  const refreshAccessToken = async (tokenToUse = refreshToken) => {
    if (!tokenToUse) return;
    
    try {
      const response = await fetch(`${config.API_BASE_URL}/um/admin/v1/refresh-token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken: tokenToUse })
      });

      const data = await response.json();

      if (data.status === 'success') {
        const { accessToken, refreshToken, expiresIn, features } = data.data;
        const expiryTime = Date.now() + expiresIn * 1000;
        const userFeatures = features || [];
        
        // Update state
        setAccessToken(accessToken);
        setRefreshToken(refreshToken);
        setTokenExpiry(expiryTime);
        setFeatures(userFeatures);
        setIsAuthenticated(true);
        
        // Update localStorage
        localStorage.setItem('accessToken', accessToken);
        localStorage.setItem('refreshToken', refreshToken);
        localStorage.setItem('tokenExpiry', expiryTime.toString());
        localStorage.setItem('features', JSON.stringify(userFeatures));
        
        // Schedule next refresh
        const timeUntilRefresh = expiresIn * 1000 - 60000;
        setTimeout(refreshAccessToken, timeUntilRefresh);
      } else {
        // If refresh fails, log out
        logout();
      }
    } catch (error) {
      console.error('Token refresh error:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  // Logout function
  const logout = async () => {
    try {
      // Only call API if we have tokens
      if (accessToken && refreshToken) {
        await fetch(`${config.API_BASE_URL}/um/admin/v1/logout`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ refreshToken })
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Always clear local state regardless of API success
      setAccessToken(null);
      setRefreshToken(null);
      setTokenExpiry(null);
      setFeatures([]);
      setAdminProfile(null);
      setIsAuthenticated(false);
      
      // Clear localStorage
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('tokenExpiry');
      localStorage.removeItem('features');
      localStorage.removeItem('adminProfile');
    }
  };

  // Get current access token (with automatic refresh if needed)
  const getAccessToken = async () => {
    const now = Date.now();
    
    // If token is expired or will expire in next 30 seconds, refresh it
    if (tokenExpiry && now > tokenExpiry - 30000) {
      await refreshAccessToken();
    }
    
    return accessToken;
  };

  // Helper function to check if user has a specific feature
  const hasFeature = (featureCode) => {
    return features.includes(featureCode);
  };

  const authContextValue = {
    isAuthenticated,
    accessToken,
    loading,
    features,
    adminProfile,
    login,
    logout,
    getAccessToken,
    hasFeature
  };

  return (
    <AuthContext.Provider value={authContextValue}>
      {children}
    </AuthContext.Provider>
  );
}; 